import { ConstellationField } from './ConstellationField';
import Styles from './Landing.module.css';
import { Children, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode, Ref } from 'react';

/** Imperative handle exposed by the deck so the header logo and card CTAs can jump to a card. */
export interface ICardDeckHandle {
  goTo: (index: number, offsetRem?: number) => void;
}

/** Inline-style anchors for a single card's two corner glows. */
interface ICardGlows {
  cyan: CSSProperties;
  orange: CSSProperties;
}

/** Props for the CardDeck flow-scroll container. */
interface ICardDeckProps {
  children: ReactNode;
  ref?: Ref<ICardDeckHandle>;
  onActiveChange?: (index: number) => void;
}

/** File-local constants for CardDeck. */
const localConstants = {
  // Fraction of the viewport height below the top edge used as the "active line": the active card is the
  // last one whose top has crossed this line. Keeps the indicator in step with what dominates the viewport.
  activeLineFraction: 0.35,
  parityDivisor: 2,
  scrollBehaviorSmooth: 'smooth',
  // Fallback px-per-rem when the computed root font-size can't be read (so the offset scroll still lands close).
  fallbackRemPx: 16,
  // The hero (first card) keeps its designed fixed glows; every other card gets random anchors.
  heroIndex: 0,
  // Random glow anchor range, percent of the card box. translate(-50%, -50%) centres each soft glow on its
  // anchor, so values just outside 0–100 let the glow bleed off the matching edge.
  glowMinPercent: -10,
  glowMaxPercent: 110,
  glowCenterTransform: 'translate(-50%, -50%)'
} as const;

/**
 * A random integer percent within the configured glow anchor range.
 *
 * @returns A percent value for use as a glow anchor coordinate.
 */
function randomGlowPercent(): number {
  const span = localConstants.glowMaxPercent - localConstants.glowMinPercent;
  return Math.round(localConstants.glowMinPercent + Math.random() * span);
}

/**
 * One randomly anchored glow: centred on its point, with the card's default corner anchoring cleared.
 *
 * @returns Inline styles positioning a single glow at a random anchor.
 */
function randomGlowStyle(): CSSProperties {
  return {
    top: `${randomGlowPercent()}%`,
    left: `${randomGlowPercent()}%`,
    bottom: 'auto',
    insetInlineEnd: 'auto',
    transform: localConstants.glowCenterTransform
  };
}

/**
 * Per-card glow placement, computed once per mount. The hero keeps its designed fixed glows (undefined ⇒ CSS defaults); every other card gets independently randomised cyan/orange anchors that
 * stay fixed across re-renders ("per card, not per draw").
 *
 * @param count - Number of cards in the deck.
 * @returns An array of glow styles per card index, or undefined for the hero.
 */
function buildGlowPositions(count: number): (ICardGlows | undefined)[] {
  const result: (ICardGlows | undefined)[] = [];
  for (let position = 0; position < count; position += 1) {
    result.push(position === localConstants.heroIndex ? undefined : { cyan: randomGlowStyle(), orange: randomGlowStyle() });
  }
  return result;
}

/**
 * Vertical flow-scroll deck.
 *
 * Cards stack in natural document flow and size to their content (with a rem floor); regular scrolling moves the page freely with no snap or input hijacking. The header logo and card CTAs call
 * `goTo`, which scrolls the target card's top into view. The active card is tracked from scroll position and reported via `onActiveChange`. Each card carries its top padding so its content
 * clears the pinned header overlay.
 *
 * @param props - Component props.
 * @returns The flow-scroll deck element.
 */
export function CardDeck(props: ICardDeckProps) {
  const { children, ref, onActiveChange } = props;

  const scrollRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [index, setIndex] = useState(0);

  const cards = useMemo(() => Children.toArray(children), [children]);
  const count = cards.length;
  const glowPositions = useMemo(() => buildGlowPositions(count), [count]);

  // Scroll a card's top into view, optionally leaving `offsetRem` of headroom above it (e.g. to clear the fixed header). The first card clamps to the very top.
  const goTo = useCallback((target: number, offsetRem = 0) => {
    const clamped = Math.max(0, Math.min(cardRefs.current.length - 1, target));
    const card = cardRefs.current[clamped];
    const scroller = scrollRef.current;
    if (!card || !scroller) return;
    const remPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || localConstants.fallbackRemPx;
    const top = Math.max(0, card.offsetTop - offsetRem * remPx);
    scroller.scrollTo({ top, behavior: localConstants.scrollBehaviorSmooth });
  }, []);

  useImperativeHandle(ref, () => ({ goTo }), [goTo]);

  // Report the active card so the landing controller can log per-card view telemetry.
  useEffect(() => {
    onActiveChange?.(index);
  }, [index, onActiveChange]);

  // Derive the active card from scroll position: the last card whose top has crossed the active line.
  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;

    const recompute = () => {
      const line = scroller.scrollTop + scroller.clientHeight * localConstants.activeLineFraction;
      let active = 0;
      for (let position = 0; position < cardRefs.current.length; position += 1) {
        const card = cardRefs.current[position];
        if (card && card.offsetTop <= line) active = position;
      }
      setIndex(active);
    };

    recompute();
    scroller.addEventListener('scroll', recompute, { passive: true });
    return () => scroller.removeEventListener('scroll', recompute);
  }, [count]);

  return (
    <div className={Styles.deck} ref={scrollRef}>
      <div className={Styles.track}>
        {cards.map((child, position) => {
          // Even-numbered cards (Card 2, 4… — the lifted alt tone) carry the drifting constellation network; the rest carry the hero's signature grid. Every card also gets the two corner glows —
          // fixed on the hero (Card 1), randomly anchored per card on every other. The final card is always forced onto the constellation tone (the signup card stays on constellations regardless of
          // where the alternation lands), so the toggle runs uninterrupted up to but excluding the last card.
          const isAltColor = position % localConstants.parityDivisor === 1 || position === count - 1;
          // The final card (signup) gets a distinct, space-like backdrop: black-on-black depth glows rather than the cyan/orange the rest share.
          const isLast = position === count - 1;
          const glows = glowPositions[position];
          return (
            <div
              key={position}
              className={`${Styles.card} ${isAltColor ? Styles.cardBgDark : Styles.cardBgBlack} ${isLast ? Styles.cardSpace : ''}`}
              data-deck-card
              ref={(element) => {
                cardRefs.current[position] = element;
              }}
            >
              <div className={Styles.cardDecor} aria-hidden="true">
                {isAltColor ? <ConstellationField /> : <div className={Styles.cardGrid} />}
                <div className={Styles.cardGlowCyan} style={glows?.cyan} />
                <div className={Styles.cardGlowOrange} style={glows?.orange} />
              </div>
              <div className={Styles.cardScroll}>{child}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
